/**
 * Raster compression for Travel Buddy: smaller IndexedDB blobs and smaller PDF exports.
 */

function loadImageElement(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image decode failed'));
    img.src = dataUrl;
  });
}

/**
 * Downscale if needed and re-encode as JPEG. Used for PDF embedding and optional storage compression.
 */
export async function compressRasterDataUrlForPdf(
  dataUrl: string,
  maxLongSidePx: number,
  quality: number
): Promise<string> {
  const img = await loadImageElement(dataUrl);
  let w = img.naturalWidth;
  let h = img.naturalHeight;
  if (w <= 0 || h <= 0) return dataUrl;

  const longSide = Math.max(w, h);
  if (longSide > maxLongSidePx) {
    const scale = maxLongSidePx / longSide;
    w = Math.max(1, Math.round(w * scale));
    h = Math.max(1, Math.round(h * scale));
  }

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return dataUrl;
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', quality);
}

function jpegDataUrlToBlob(dataUrl: string): Blob {
  const comma = dataUrl.indexOf(',');
  const base64 = comma >= 0 ? dataUrl.slice(comma + 1) : '';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: 'image/jpeg' });
}

/**
 * Shrink large photo uploads before IndexedDB — keeps exports and localStorage previews lighter.
 * PDF uploads are returned unchanged.
 */
export async function compressImageFileForStorage(file: File): Promise<File> {
  const mime = file.type || '';
  if (!mime.startsWith('image/') || mime === 'image/gif') return file;

  const threshold = 450 * 1024;
  if (file.size <= threshold) return file;

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(typeof r.result === 'string' ? r.result : '');
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
  if (!dataUrl) return file;

  try {
    const jpeg = await compressRasterDataUrlForPdf(dataUrl, 1920, 0.82);
    const blob = jpegDataUrlToBlob(jpeg);
    if (blob.size >= file.size) return file;
    const base = file.name.replace(/\.[^.]+$/, '') || 'receipt';
    return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
  } catch {
    return file;
  }
}
